// Chat markdown renderer. Extracted from ChatDrawer.tsx.
//
// react-markdown + remark-gfm gives us tables, headings, lists, bold, code
// blocks, links, etc. The `components` overrides give each element Tailwind
// classes that fit the chat-bubble aesthetic — tight vertical rhythm,
// smaller-than-prose sizing, scrollable tables.

import { type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MD_COMPONENTS = {
  h1: ({ children }: { children?: ReactNode }) => <h1 className="text-base font-bold mt-3 mb-1">{children}</h1>,
  h2: ({ children }: { children?: ReactNode }) => <h2 className="text-sm font-bold mt-3 mb-1">{children}</h2>,
  h3: ({ children }: { children?: ReactNode }) => <h3 className="text-sm font-semibold mt-2 mb-1 text-slate-800">{children}</h3>,
  h4: ({ children }: { children?: ReactNode }) => <h4 className="text-xs font-semibold mt-2 mb-1 text-slate-700">{children}</h4>,
  p: ({ children }: { children?: ReactNode }) => <p className="mb-2 leading-snug">{children}</p>,
  ul: ({ children }: { children?: ReactNode }) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }: { children?: ReactNode }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }: { children?: ReactNode }) => <li className="leading-snug">{children}</li>,
  strong: ({ children }: { children?: ReactNode }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }: { children?: ReactNode }) => <em className="italic">{children}</em>,
  hr: () => <hr className="my-2 border-slate-300" />,
  blockquote: ({ children }: { children?: ReactNode }) =>
    <blockquote className="border-l-2 border-slate-300 pl-2 my-2 text-slate-700 italic">{children}</blockquote>,
  // BUG C fix — react-markdown v10+ no longer passes `inline`. Distinguish
  // by className: fenced code blocks (```js ...```) get a language-* class,
  // single-backtick inline code gets no className. Without this fix every
  // `bi_xxx` ID the model cited rendered as a full-width code block.
  code: ({ children, className }: { children?: ReactNode; className?: string }) => {
    const isBlock = typeof className === 'string' && /^language-/.test(className);
    return isBlock
      ? <code className={`block bg-slate-50 border border-slate-200 rounded p-2 my-2 overflow-x-auto text-[11px] font-mono whitespace-pre ${className ?? ''}`}>{children}</code>
      : <code className="bg-slate-200 text-slate-800 rounded px-1 text-[11px] font-mono">{children}</code>;
  },
  pre: ({ children }: { children?: ReactNode }) => <pre className="my-2 overflow-x-auto">{children}</pre>,
  a: ({ href, children }: { href?: string; children?: ReactNode }) =>
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-navy underline hover:no-underline">{children}</a>,
  table: ({ children }: { children?: ReactNode }) =>
    <div className="overflow-x-auto my-2"><table className="text-xs border-collapse border border-slate-300">{children}</table></div>,
  thead: ({ children }: { children?: ReactNode }) => <thead className="bg-slate-100">{children}</thead>,
  tbody: ({ children }: { children?: ReactNode }) => <tbody>{children}</tbody>,
  tr: ({ children }: { children?: ReactNode }) => <tr className="border-b border-slate-200">{children}</tr>,
  th: ({ children }: { children?: ReactNode }) => <th className="border border-slate-300 px-2 py-1 text-left font-semibold">{children}</th>,
  td: ({ children }: { children?: ReactNode }) => <td className="border border-slate-300 px-2 py-1 align-top">{children}</td>,
};

export function MarkdownBlock({ text }: { text: string }): ReactNode {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
      {text}
    </ReactMarkdown>
  );
}
